import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaUserPlus, FaKey, FaExchangeAlt, FaPlus, FaSearch, FaUsers, FaTrash, FaUserEdit, FaPeopleArrows, FaMobileAlt } from "react-icons/fa";
import { SiZendesk, SiJira } from "react-icons/si";
import ResetPasswordModal from './../modals/support/ResetPasswordModal';
import CreateUserModal from "./../modals/support/CreateUserModal";
import ActionTransfersModal from "./../modals/support/ActionTransfersModal";
import CreateSiteModal from './../modals/support/CreateSiteModal';
import ManageGroupModal from './../modals/support/ManageGroupModal';
import UserDrawer from './UserDrawer';
import "./../../styles/SupportDashboard.css";
import UserService from './../../services/UserService';
import SiteService from './../../services/SiteService';
import UserTransferService from "./../../services/UserTransferService";
import logService from "./../../services/LogService";
import BulkCreateUsersModal from './../modals/support/BulkCreateUsersModal';
import { useNavigate, useLocation } from "react-router-dom";
import {
  isToday,
  formatAuditTime,
  extractTargetFromDescription,
  extractRequesterFromDescription,
} from './../../utils/auditUtils';
import EditUserModal from './../modals/support/EditUserModal';
import BulkDeviceTransferModal from './../modals/support/BulkDeviceTransferModal';
import DeleteUserModal from './../modals/support/DeleteUserModal';
import DeviceTelemetryPage from "./../DeviceTelemetryPage";

export default function SupportDashboardPage() {
  const [stats, setStats] = useState({
    usersCreatedToday: 0,
    passwordResetsToday: 0,
    pendingTransfers: 0,
    groupsTotal: 0
  });

  const [activity, setActivity] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [openManageGroup, setOpenManageGroup] = useState(false);
  const [openBulkDeviceTransfer, setOpenBulkDeviceTransfer] = useState(false);
  const [sites, setSites] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerUser, setDrawerUser] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(1);
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [expandedActivityId, setExpandedActivityId] = useState(null);
  const [activityDays, setActivityDays] = useState(1);
  const [openBulkCreate, setOpenBulkCreate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const isTelemetryView = location.pathname.startsWith("/support-dashboard/device-telemetry");
  const toggleActivity = useCallback((id) => {
    setExpandedActivityId((prev) => (prev === id ? null : id));
  }, []);


function normalizeArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

const loadAudit = useCallback(async () => {
  setActivity([]);
  setStats(prev => ({
    ...prev,
    usersCreatedToday: 0,
    passwordResetsToday: 0,
    pendingTransfers: 0,
  }));

  const interval = activityDays;

  const [createR, resetR, transferR, renameR, deleteGroupR] = await Promise.allSettled([
    logService.getAuditLog("UserCreation", interval),
    logService.getAuditLog("ResetPassword", interval),
    selectedGroupId == null ? Promise.resolve([]) : UserTransferService.getActionedTransfers(selectedGroupId, interval),
    logService.getAuditLog("SiteNameUpdate", interval),
    logService.getAuditLog("SiteDeletion", interval),
  ]);

  const createLogs = normalizeArray(createR.status === "fulfilled" ? createR.value : []);
  const resetLogs = normalizeArray(resetR.status === "fulfilled" ? resetR.value : []);
  const transferLogs = normalizeArray(transferR.status === "fulfilled" ? transferR.value : []);
  const renameLogs = normalizeArray(renameR.status === "fulfilled" ? renameR.value : []);
  const deleteGroupLogs = normalizeArray(deleteGroupR.status === "fulfilled" ? deleteGroupR.value : []);

  if (createR.status === "rejected") console.error("UserCreation logs failed:", createR.reason);
  if (resetR.status === "rejected") console.error("ResetPassword logs failed:", resetR.reason);
  if (transferR.status === "rejected") console.error("Actioned transfers failed:", transferR.reason);
  if (renameR.status === "rejected") console.error("Group rename logs failed:", renameR.reason);
  if (deleteGroupR.status === "rejected") console.error("Group deletion logs failed:", deleteGroupR.reason);

  setStats(prev => ({
    ...prev,
    usersCreatedToday: createLogs.length,
    passwordResetsToday: resetLogs.length,
    pendingTransfers: transferLogs.length,
  }));

  const groupActivity = (logs, type, prefix) => logs.map((entry) => ({
    id: `${prefix}-${entry.id}`,
    type,
    who: entry.actionByUsername || "—",
    target: entry.description || "Group updated",
    when: formatAuditTime(entry.datetime),
    datetime: entry.datetime,
    description: entry.description,
    rawId: entry.id,
  }));

  const rows = [
    ...groupActivity(renameLogs, "RENAME_GROUP", "GR"),
    ...groupActivity(deleteGroupLogs, "DELETE_GROUP", "GD"),
    ...createLogs.map((x) => ({
      id: `CU-${x.id}`,
      type: "CREATE_USER",
      who: extractRequesterFromDescription(x.description) || "—",
      target: extractTargetFromDescription(x.description) || "—",
      when: formatAuditTime(x.datetime),
      datetime: x.datetime,
      description: x.description,
      rawId: x.id,
    })),

    ...resetLogs.map((x) => ({
      id: `RP-${x.id}`,
      type: "RESET_PASSWORD",
      who: extractRequesterFromDescription(x.description) || "—",
      target: extractTargetFromDescription(x.description) || "—",
      when: formatAuditTime(x.datetime),
      datetime: x.datetime,
      description: x.description,
      rawId: x.id,
    })),

    ...transferLogs.map((x) => ({
      id: `TR-${x.id}`,
      type: "TRANSFER",
      who: (x.actionBy) || "—",
      target: extractTargetFromDescription(x.visorId) || "—",
      when: formatAuditTime(x.updateDate),
      datetime: x.updateDate,
      description: x.description,
      rawId: x.id,
    })),
  ]
    .sort((a, b) => new Date(b.datetime) - new Date(a.datetime))
    .slice(0, 25);

  setActivity(rows);
}, [activityDays, selectedGroupId]);


useEffect(() => {
  loadAudit();
}, [loadAudit]);




    useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await SiteService.get();
        const data = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
     setSites(data);
      } catch (error) {
        console.error('Error fetching sites:', error);
      }
    };
    fetchSites();
  }, []);

const refreshAudit = useCallback(async () => {
  try {
    const interval = 0;
    const [createRes, resetRes] = await Promise.all([
      logService.getAuditLog("UserCreation", interval),
      logService.getAuditLog("ResetPassword", interval),
    ]);

    const createLogs = normalizeArray(createRes);
    const resetLogs  = normalizeArray(resetRes);

    setStats((prev) => ({
      ...prev,
      usersCreatedToday: createLogs.filter((x) => isToday(x.datetime)).length,
      passwordResetsToday: resetLogs.filter((x) => isToday(x.datetime)).length,
    }));
  } catch (e) {
    console.error("Failed to refresh audit logs:", e);
  }
}, []);



  const filteredActivity = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return activity;
    return activity.filter(a =>
      `${a.type} ${a.who} ${a.target} ${a.when}`.toLowerCase().includes(q)
    );
  }, [activity, globalSearch]);

const refreshTransfers = useCallback(async () => {
  if (selectedGroupId == null) return;

  setTransfersLoading(true);

  try {
    const response = await UserTransferService.getAllTransfers(selectedGroupId);

    const data = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.data?.data)
      ? response.data.data
      : [];

    setTransfers(data);

  } catch (e) {
    console.error("Failed to load transfers", e);
    setTransfers([]);
  } finally {
    setTransfersLoading(false);
  }
}, [selectedGroupId]);

const refreshSites = useCallback(async () => {
  try {
    const response = await SiteService.get();
       const data = Array.isArray(response)
     ? response
     : Array.isArray(response?.data)
     ? response.data
     : Array.isArray(response?.data?.data)
     ? response.data.data
     : [];
   setSites(data);
  } catch (error) {
    console.error('Error fetching sites:', error);
  }
}, []);

  useEffect(() => {
  if (openTransfer && selectedGroupId != null) {
    refreshTransfers();
  }
}, [openTransfer, selectedGroupId, refreshTransfers]);
  useEffect(() => {
    if (!openTransfer) return;
    refreshTransfers();
  }, [openTransfer, refreshTransfers]);

  useEffect(() => {
    if (!openTransfer) return;
    refreshTransfers();
  }, [selectedGroupId, openTransfer, refreshTransfers]);
useEffect(() => {
  if (selectedGroupId != null) {
    refreshTransfers();
  }
}, [selectedGroupId, refreshTransfers]);
const openDrawerForUser = (user) => {
  setDrawerUser(user);
  setDrawerOpen(true);
};

const onGlobalSearch = async () => {
  const q = globalSearch.trim();
  if (!q) return;

  const user = {
    username: q,
    userType: "Support",
    groupName: "West Midlands",
    status: "Active",
    lastLogin: "Today 10:12",
    platform: "mobile"
  };

  openDrawerForUser(user);
};

useEffect(() => {
  setGlobalSearch("");
  setExpandedActivityId(null);
}, [activityDays]);


  return (
    <div className="sd-page">
      <div className="sd-shell">
        <Sidebar
  onCreate={() => setOpenCreate(true)}
  onBulkCreate={() => setOpenBulkCreate(true)}
  onEdit={() => setOpenEdit(true)}
  onReset={() => setOpenReset(true)}
  onTransfer={() => setOpenTransfer(true)}
  onAddGroup={() => setOpenAddGroup(true)}
  onManageGroup={() => setOpenManageGroup(true)}
  onDelete={() => setOpenDelete(true)}
  onBulkDeviceTransfer={() => setOpenBulkDeviceTransfer(true)}
  onEditUser={() => setOpenEdit(true)}
  telemetryActive={isTelemetryView}

/>

        <div className="sd-main">
          {isTelemetryView ? (
            <DeviceTelemetryPage embedded />
          ) : (
            <>
          <Topbar
            value={globalSearch}
            onChange={setGlobalSearch}
            onSearch={onGlobalSearch}
            onCreate={() => setOpenCreate(true)}
            onEdit={() => setOpenEdit(true)}
          />

          <div className="sd-content">
            <div className="sd-grid">
              <section className="sd-panel sd-hero">
                <div className="sd-hero-head">
                  <div>
                    <h1 className="sd-hero-title">Support Console</h1>
                    <div className="sd-hero-sub">
                      Create users, reset passwords, action transfers, and manage groups.
                    </div>
                  </div>

                  <div className="sd-hero-actions">
                    <ActionButton icon={<FaUserPlus />} label="Create user" onClick={() => setOpenCreate(true)} />
                    <ActionButton icon={<FaUserEdit />} label="Edit user" onClick={() => setOpenEdit(true)} />
                    <ActionButton icon={<FaUsers />} label="Bulk create users" onClick={() => setOpenBulkCreate(true)} />
                    <ActionButton icon={<FaPeopleArrows />} label="Bulk device transfer" onClick={() => setOpenBulkDeviceTransfer(true)} />
                    <ActionButton icon={<FaKey />} label="Reset password" onClick={() => setOpenReset(true)}/>
                    <ActionButton icon={<FaExchangeAlt />} label="Action transfer" onClick={() => setOpenTransfer(true)} />
                    <ActionButton icon={<FaTrash />} label="Delete user" onClick={() => setOpenDelete(true)} />
                    <ActionButton icon={<FaPlus />} label="Add group" onClick={() => setOpenAddGroup(true)} />
                    <ActionButton icon={<FaUserEdit />} label="Manage group" onClick={() => setOpenManageGroup(true)} />
                  </div>
                </div>

                <div className="sd-metrics">
                  <MetricCard icon={<FaUsers />} title="Users created" value={stats.usersCreatedToday} hint="Today" accent="primary" />
                  <MetricCard icon={<FaKey />} title="Password resets" value={stats.passwordResetsToday} hint="Today" accent="good" />


                  <MetricCard
  icon={<FaExchangeAlt />}
  title="Pending transfers"
  value={transfers.length}
  hint="Awaiting action"
  accent="warn"
  isClickable={transfers.length > 0}
  onClick={() => navigate("/transfers-users?tab=transfers")}
/>

                </div>
              </section>

<section className="sd-panel">
  <div className="sd-panel-head sd-activity-head">
    <div>
      <h2 className="sd-hero-title">Recent activity</h2>
    </div>

    <div className="sd-activity-controls">
      <label className="sd-activity-label">
        Range
        <select
          className="sd-activity-select"
          value={activityDays}
          onChange={(e) => setActivityDays(Number(e.target.value))}
        >
          {Array.from({ length: 60 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              Last {d} day{d === 1 ? "" : "s"}
            </option>
          ))}
        </select>
      </label>
    </div>
  </div>

  <div className="sd-activity-list">
    {filteredActivity.length === 0 ? (
      <div className="sd-empty">No activity matches your search.</div>
    ) : (
      filteredActivity.map((item) => (
        <ActivityRow
          key={item.id}
          item={item}
          isOpen={expandedActivityId === item.id}
          onToggle={() => toggleActivity(item.id)}
        />
      ))
    )}
  </div>
</section>

              {selectedUser && (
                <section className="sd-panel">
                  <div className="sd-panel-head">
                    <h2 className="sd-title">Selected user</h2>
                    <div className="sd-subtitle">{selectedUser.username}</div>
                  </div>
                </section>
              )}
            </div>
          </div>
            </>
          )}

          <CreateUserModal
            isOpen={openCreate}
            onClose={() => setOpenCreate(false)}
            sites={sites}
            onSubmit={async (payload) => {
                if (payload.platform === 'mobile') {
                  await UserService.createMobileUser(payload);
                } 
                else if (payload.platform === 'pc') {
                  await UserService.createPCUser(payload);
                }
              await refreshAudit();
            }}
          />

          <EditUserModal
            isOpen={openEdit}
            onClose={() => setOpenEdit(false)}
            user={selectedUser}
            sites={sites}
            onSubmit={async (payload, platform) => {
              if (platform === 'mobile') {
                await UserService.updateMobileUser(payload);
              }
              if (platform === 'pc') {
                await UserService.updatePCInstallAccount(payload);
              }
              await refreshAudit();
            }}
          />

          <BulkCreateUsersModal
            isOpen={openBulkCreate}
            onClose={() => setOpenBulkCreate(false)}
            sites={sites}
            onSubmit={async (rows) => {
                            
              await UserService.bulkCreateMobileUsers({ users: rows });
              
              await refreshAudit();
            }}
          />

          <BulkDeviceTransferModal 
            isOpen={openBulkDeviceTransfer} 
            onClose={() => setOpenBulkDeviceTransfer(false)} 
            sites={sites}
          /> 

          <ResetPasswordModal
            isOpen={openReset}
            onClose={() => setOpenReset(false)}
            user={drawerUser}
            onSubmit={async (payload) => {
              await UserService.resetPasswordAdmin(payload);
              await refreshAudit();
            }}
          />
          
          <ActionTransfersModal
        isOpen={openTransfer}
        onClose={() => setOpenTransfer(false)}
        sites={sites}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
        transfers={transfers}
        loading={transfersLoading}
        onRefresh={refreshTransfers}
        onSubmit={async (payload) => {
          await UserTransferService.actionTransfer(payload);
          await refreshTransfers();
        }}
      />

<CreateSiteModal
  isOpen={openAddGroup}
  onClose={() => setOpenAddGroup(false)}
  sites={sites}
  onSubmit={async (payload) => {
    await SiteService.create(payload);
    await refreshSites();
  }}
/>

<ManageGroupModal
  isOpen={openManageGroup}
  onClose={() => setOpenManageGroup(false)}
  sites={sites}
  onRename={async (id, siteName) => {
    await SiteService.updateName(id, siteName);
    setSites(previous => previous.map(site => String(site.id) === String(id) ? { ...site, sitename: siteName } : site));
    await Promise.all([refreshSites(), loadAudit()]);
  }}
  onDelete={async (id) => {
    await SiteService.deleteGroup(id);
    setSites(previous => previous.filter(site => String(site.id) !== String(id)));
    if (String(selectedGroupId) === String(id)) setSelectedGroupId(null);
    await Promise.all([refreshSites(), loadAudit()]);
  }}
/>

<DeleteUserModal
  isOpen={openDelete}
  onClose={() => setOpenDelete(false)}
  onSubmit={async ({ username, platform }) => {
    if (platform === "mobile") {
      await UserService.deleteMobileUserAccount(username);
    } else {
      await UserService.deletePCInstallAccount(username);
    }

    await refreshAudit();
  }}
/>

<UserDrawer
  isOpen={drawerOpen}
  user={drawerUser}
  onClose={() => setDrawerOpen(false)}
  onResetPassword={() => {
    setDrawerOpen(false);     
    setOpenReset(true);       
  }}
  onTransfer={() => { setOpenTransfer(true); }}
  onDelete={() => {
    setDeleteUser(drawerUser);
    setDrawerOpen(false);
    setOpenDelete(true);
  }}
/>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ onCreate, onReset, onTransfer, onAddGroup, onManageGroup, onBulkCreate, onDelete, onEditUser, onBulkDeviceTransfer, telemetryActive }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <aside className="sd-sidebar">
      <div className="sd-brand">
        <div>
          <div className="sd-brand-title">Support</div>
          <div className="sd-brand-sub">Operations console</div>
        </div>
      </div>

      <nav className="sd-nav">
        <NavGroup title="Quick actions">
          <NavItem icon={<FaUserPlus />} label="Create user" onClick={onCreate} />
          <NavItem icon={<FaUsers />} label="Bulk create users" onClick={onBulkCreate} />
          <NavItem icon={<FaKey />} label="Reset password" onClick={onReset} />
          <NavItem icon={<FaExchangeAlt />} label="Action transfer" onClick={onTransfer} />
          <NavItem icon={<FaPlus />} label="Add group" onClick={onAddGroup} />
          <NavItem icon={<FaUserEdit />} label="Manage group" onClick={onManageGroup} />
          <NavItem icon={<FaTrash />} label="Delete user" onClick={onDelete} />
          <NavItem icon={<FaUserEdit />} label="Edit user" onClick={onEditUser} />
          <NavItem icon={<FaPeopleArrows />} label="Bulk device transfer" onClick={onBulkDeviceTransfer} />
        </NavGroup>

        <NavGroup title="Navigate">
          <NavItem
            icon={<FaMobileAlt />}
            label="Device telemetry"
            active={telemetryActive}
            onClick={() => navigate("/support-dashboard/device-telemetry")}
          />
          <NavItem
            icon={<FaUsers />}
            label="Users"
            active={location.pathname.startsWith("/users")}
            onClick={() => navigate("/users")}
          />
          <NavItem
            icon={<FaExchangeAlt />}
            label="Transfers"
            active={location.pathname.startsWith("/transfers-users")}
            onClick={() => navigate("/transfers-users")}
          />
          <NavItem
          icon={<SiZendesk />}
            label="Zendesk"
            active={""}
            onClick={() => window.open("https://smoothwallsupport.zendesk.com/agent/home/tickets", "_blank")}
          />
          
          <NavItem
          icon={<SiJira />}
            label="Jira"
            active={""}
            onClick={() => window.open("https://familyzone.atlassian.net/jira", "_blank")}
          />

        </NavGroup>
      </nav>
    </aside>
  );
}

function Topbar({ value, onChange, onSearch, onCreate }) {
  return (
    <header className="sd-topbar">
      <div className="sd-search">
        <FaSearch className="sd-search-icon" />
        <input
          className="sd-search-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search activity or paste visorId / username…"
          onKeyDown={(e) => e.key === "Enter" && onSearch?.()}
          autoComplete="off"
        />
        {value && (
          <button className="sd-clear" onClick={() => onChange("")} aria-label="Clear">
            ✕
          </button>
        )}
      </div>
    </header>
  );
}

function MetricCard({
  icon,
  title,
  value,
  hint,
  accent = "primary",
  isClickable = false,
  onClick,
}) {
  const clickable = isClickable && typeof onClick === "function";

  return (
    <div
      className={`sd-metric sd-metric-${accent} ${clickable ? "is-clickable" : ""}`}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      aria-disabled={clickable ? undefined : true}
    >
      <div className="sd-metric-icon">{icon}</div>
      <div className="sd-metric-body">
        <div className="sd-metric-title">{title}</div>
        <div className="sd-metric-value">{value}</div>
        <div className="sd-metric-hint">{hint}</div>
      </div>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button className="sd-action-btn" onClick={onClick} type="button">
      <span className="sd-action-ic">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function ActivityRow({ item, isOpen, onToggle }) {
  const badge = badgeFor(item.type);
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div className={`sd-activity-card ${isOpen ? "is-open" : ""}`}>
      <div className={`sd-activity-row ${isOpen ? "is-open" : ""}`}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onClick={onToggle}
        onKeyDown={onKeyDown}
      >
        <span className={`sd-badge ${badge.className}`}>{badge.text}</span>

        <div className="sd-activity-main">
          <div className="sd-activity-target">{item.target}</div>
          <div className="sd-activity-meta">
            <span>{item.who}</span>
            <span className="sd-dot">•</span>
            <span>{item.when}</span>
          </div>
        </div>

        <span className={`sd-activity-chevron ${isOpen ? "is-open" : ""}`}>
          ▾
        </span>
      </div>

      <div className={`sd-activity-details ${isOpen ? "is-open" : ""}`}>
        <div className="sd-activity-details-clip">
          <div className="sd-activity-details-inner">
            <div className="sd-activity-detail-grid">
              <div className="sd-detail">
                <div className="sd-detail-label">Event</div>
                <div className="sd-detail-value">{badge.text}</div>
              </div>
              <div className="sd-detail">
                <div className="sd-detail-label">ID</div>
                <div className="sd-detail-value">{item.id}</div>
              </div>
              <div className="sd-detail">
                <div className="sd-detail-label">Exact time</div>
                <div className="sd-detail-value">
                  {new Date(item.datetime).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="sd-activity-description">
              <div className="sd-detail-label">Description</div>
              <div className="sd-detail-value sd-mono">
                {item.description || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function badgeFor(type) {
  switch (type) {
    case "CREATE_USER": return { text: "User Created", className: "is-primary" };
    case "RESET_PASSWORD": return { text: "Password", className: "is-good" };
    case "TRANSFER": return { text: "Completed Transfer", className: "is-warn" };
    case "RENAME_GROUP": return { text: "Group Renamed", className: "is-primary" };
    case "DELETE_GROUP": return { text: "Group Deleted", className: "is-warn" };
    case "ADD_GROUP": return { text: "Groups", className: "is-muted" };
    default: return { text: "Action", className: "is-muted" };
  }
}

function NavGroup({ title, children }) {
  return (
    <div className="sd-nav-group">
      <div className="sd-nav-title">{title}</div>
      <div className="sd-nav-items">{children}</div>
    </div>
  );
}

function NavItem({ icon, label, onClick, active }) {
  return (
    <button
      className={`sd-nav-item ${active ? "is-active" : ""}`}
      onClick={onClick}
      type="button"
    >
      <span className="sd-nav-icon">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
